import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import TaskTable from '../TaskTable';
import { ClientTaskManagerLayout, PageHeader, PageLayout } from '../TaskManager.styles';
import { WrappedSpinner } from 'spinner';
import { usePaginatedTasks } from '../usePaginatedTasks';
import ErrorState from 'task-manager/ErrorState';
import TaskFilters from 'task-manager/TaskFilters';
import { useQueryParams } from 'use-query-params';
import TaskTableSummary from 'task-manager/TaskTableSummary';
import { Box } from '@material-ui/core';
import { Task } from 'task-manager/types';
import TaskSidebar from 'task-manager/task-sidebar/TaskSidebar';
import { TaskSummary } from './TaskSummary';

const ClientTaskManager = ({ clientId }: { clientId: string }) => {
  const [selectedTask, setSelectedTask] = useState<Task>();
  const tableRef = useRef<HTMLTableElement>(null);

  const location = useLocation();

  useEffect(() => {
    if (!location.search) return;
    setSelectedTask(undefined);
  }, [location.search]);

  const [_, setQuery] = useQueryParams();

  const { tasks, error, loading, getNextPage, getPrevPage, total } = usePaginatedTasks({});

  React.useEffect(() => {
    if (clientId) {
      setQuery({ clientIds: [clientId] });
    }
  }, [clientId, setQuery]);

  const handleRowClick = (task: Task) => {
    if (task.id === selectedTask?.id) {
      setSelectedTask(undefined);
      return;
    }
    setSelectedTask(task);
  };

  if (error && !tasks) {
    return (
      <PageLayout>
        <ErrorState
          description={error.message}
          title="Uh oh! The following error was encountered while fetching the data for this page."
        />
      </PageLayout>
    );
  }

  return (
    <ClientTaskManagerLayout>
      <PageLayout>
        <PageHeader>Task Manager</PageHeader>
        <TaskSummary clientId={clientId} />
        <Box sx={{ margin: '0 16px' }}>
          <TaskFilters isClientsTaskManager />
        </Box>
        {loading && !tasks ? (
          <WrappedSpinner></WrappedSpinner>
        ) : (
          <>
            <Box sx={{ margin: '0px 19px' }}>
              <TaskTableSummary total={total} />
            </Box>
            <TaskTable
              tasks={tasks || []}
              paginationFns={{ getNextPage, getPrevPage }}
              isClientTaskManager
              onRowClick={handleRowClick}
              ref={tableRef}
            />
            {selectedTask ? (
              <TaskSidebar
                task={selectedTask}
                dismissSidebar={() => setSelectedTask(undefined)}
                preventDismiss={event =>
                  tableRef.current && event.composedPath().includes(tableRef.current)
                }
                hideActionButtons
              />
            ) : null}
          </>
        )}
      </PageLayout>
    </ClientTaskManagerLayout>
  );
};

export default ClientTaskManager;
